
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import DateSelector from '@/components/generic/DateSelector.vue'
import fillgridService from '@/services/FillgridService'
import { PageRequest } from '@/types/Support'
import { FillgridColumn, FillgridViewFilters } from '@/types/Fillgrid'
import { NUMBER } from '@/types/GeneralConstants'
import userInfoStore from '@/store/userInfo'
import { PermissionNames } from '@/types/Auth'

// eslint-disable-next-line
enum VIEW_STATE {
  // eslint-disable-next-line
  INIT, SUCCESS, FAILED
}

@Component({
  components: {
    DateSelector
  }
})
export default class FillgridFilters extends Vue {
  @Prop({ required: true })
  template: number

  @Prop({ required: false, default: [] })
  identColumns: any[]

  @Prop({ required: false, default: [] })
  columns: any[]

  @Prop({ required: false, default: [] })
  noVisibleColumns: string[]

  @Prop({ required: false, default: 0 })
  viewId: number

  @Prop({ required: false, default: false })
  autoSave: boolean

  columnsOptions: any[] = []
  noColumns: string[] = []

  savedViewSelected = 0
  nameViewsToSave = ''
  savedViewNameSelected = ''
  viewUpdated = 0

  savedViewsList: any[] = []
  selectedColumns: any[] = []
  backupSelectedColumns: any[] = []
  allColumnsSelected = false

  predeterminedSelected = false
  selectIndeterminated: boolean = false

  pageRequest = new PageRequest<FillgridViewFilters>(100)

  created () {
    this.noColumns = this.noVisibleColumns.concat(['_checkbox'])
    if (!this.fillMetadataPermission()) {
      this.noColumns = this.noColumns.concat(['_name', '_sourceId'])
    }
    this.columnsOptions = this.columns.reduce((arr: any[], it: any) => {
      return !this.noColumns.includes(it.name) ? arr.concat(Object.freeze({
        text: (it.label && it.label.trim()) || it.name,
        value: it.name
      })) : arr
    }, [])
    // We set all the columns as selected less the 'Id' column by default
    this.selectedColumns = this.columnsOptions
      .map(col => col.value)
      .filter((colName) => (colName !== '_id' && colName !== '_sourceId' && colName !== '_name'))
    this.loadViews()
  }

  /**
   * After three seconds we close the view feedback
   */
  closeViewFeedback () {
    setTimeout(() => {
      this.setViewUpdated(VIEW_STATE.INIT)
    }, 3000)
  }

  /**
   * Set the value 'state' to viewUpdated
   * @param state 0: init, 1: done, 2: error
   */
  setViewUpdated (state: number) {
    this.viewUpdated = state
  }

  @Watch('autoSave')
  watchAutoSaveFlag (save: boolean): void {
    const changesInView = JSON.stringify(this.selectedColumns) !== JSON.stringify(this.backupSelectedColumns)
    if (save && changesInView) {
      this.nameViewsToSave = this.savedViewNameSelected
      this.saveViews(this.savedViewSelected)
        .then(() => {
          this.$emit('auto-saved')
        })
    }
  }

  /**
   * Listene to the changes for the prop 'viewId'.
   * This only could change by the father component 'Fillgrid'
   *
   * @param n current value of 'viewId'
   * @param o old value of 'viewId'
   */
  @Watch('viewId')
  watchChange (n: number, o: number) {
    this.savedViewSelected = n
  }

  @Watch('savedViewSelected')
  watchSavedViewSelected (value: any) {
    this.loadViewById(value)
  }

  /**
   * Set the value of the columns selected
   * If 'value' is true set all columns as selected
   * else clear the columns selected
   * @param value true or false
   */
  toggleAllSelected (value: boolean) {
    this.selectedColumns = value ? this.columnsOptions.map(it => it.value) : []
  }

  /**
   * Listened to changes on select columns
   *
   * @param newValues last columns selected
   * @param oldValues previous columns selected
   */
  @Watch('selectedColumns')
  changeColumnsSelected (newValues: any[], oldValues: any[]) {
    if (newValues.length === 0) {
      this.selectIndeterminated = false
      this.allColumnsSelected = false
    } else if (newValues.length === this.columnsOptions.length) {
      this.selectIndeterminated = false
      this.allColumnsSelected = true
    } else {
      this.selectIndeterminated = true
      this.allColumnsSelected = false
    }
    this.apply()
  }

  loadViews () {
    fillgridService.getViews(this.template, this.pageRequest).then((viewsPage) => {
      this.savedViewsList = this.savedViewsList.concat(...viewsPage.results)
      if (this.savedViewsList.length < viewsPage.total) {
        this.pageRequest.page++
        this.loadViews()
      } else {
        const predetermined = this.savedViewsList.find(it => it.predetermined)
        if (predetermined) {
          this.savedViewSelected = predetermined.id
          this.$bvToast.toast('Ha sido aplicada la vista predeterminada: ' + predetermined.name, {
            title: 'Vista aplicada',
            solid: true,
            autoHideDelay: 4000,
            variant: 'info'
          })
        }
      }
    })
  }

  clearViews () {
    this.allColumnsSelected = false
    this.predeterminedSelected = false
    if (this.savedViewSelected) this.savedViewSelected = 0
    else this.loadViewById(0)
  }

  async loadViewById (viewId: number) {
    if (viewId === 0) {
      this.selectedColumns = []
    } else {
      const view = this.savedViewsList.find(it => it.id === viewId)
      this.savedViewNameSelected = view.name
      if (!view.columns) {
        const data = await fillgridService.getViewById(this.template, viewId)
        const idents = data.metadata.filter((column: string) => this.identColumns.includes(column))
        const metadata = data.metadata.filter((column: string) => !this.identColumns.includes(column))
        view.columns = idents // first add ident values
        view.columns = view.columns.concat(metadata.map((meta: string) => `_${meta}`)) // Second add metadata values
        const values = data.values.filter((column) => !view.columns.includes(column))
        view.columns = view.columns.concat(values) // Thrid add the rest of values
      }
      this.predeterminedSelected = view.predetermined
      this.selectedColumns = view.columns
      this.updateBackupColumnsSelected([...this.selectedColumns])
    }
    this.nameViewsToSave = ''
    this.apply()
  }

  updateBackupColumnsSelected (columns: any[]): void {
    this.backupSelectedColumns = columns
  }

  fillMetadataPermission () {
    return userInfoStore.hasPermission(PermissionNames.FILLSMETADATA_VIEW)
  }

  openDeleteSavedViews () {
    this.$bvModal.msgBoxConfirm('¿Deseas elimnar permanentemente la vista "' + this.savedViewNameSelected + '"?', {
      title: 'Confirmación',
      size: 'sm',
      okVariant: 'danger',
      okTitle: 'Sí',
      cancelTitle: 'No',
      footerClass: 'p-2',
      hideHeaderClose: false,
      centered: true
    })
      .then(async (confirmed) => {
        if (confirmed) {
          await fillgridService.deleteView(this.template, this.savedViewSelected)
          this.savedViewsList.splice(this.savedViewsList.indexOf(this.savedViewsList.find(it => it.id === this.savedViewSelected)), 1)
          this.clearViews()
        }
      })
  }

  openSaveViewModal () {
    if (!this.selectedColumns.length) return
    if (this.savedViewSelected) {
      this.nameViewsToSave = this.savedViewNameSelected
    }
    this.$bvModal.show('save-views-modal')
  }

  focusNameViewToSaveInput (): void {
    const inputSaveFilters = document.getElementById('input-save-filters')
    inputSaveFilters?.focus()
  }

  addIdentColumns (idents: any[], columns: any[]): void {
    for (const ident of idents) {
      if (!columns.includes(ident)) columns.unshift(ident)
    }
  }

  addIdentTags (nIdents: number, columns: any[]): void {
    for (let i = 0; i < nIdents; i++) {
      columns.unshift(`ident${i}`)
    }
  }

  removeIdentColumns (idents: any[], columns: any[]): void {
    for (const ident of idents) {
      const index = columns.findIndex((column) => column === ident)
      if (index > NUMBER.MINUS_ONE) columns.splice(index, NUMBER.ONE)
    }
  }

  async saveViews (idToUpdate: number) {
    if (!this.nameViewsToSave.trim().length) {
      this.focusNameViewToSaveInput()
      return
    }

    this.addIdentColumns(this.identColumns, this.selectedColumns)
    const metadataColumns = this.selectedColumns.reduce((arr: string[], it: string) => it.startsWith('_') ? arr.concat(it.substring(1)) : arr, [])
    this.addIdentTags(this.identColumns.length, metadataColumns)
    const valuesColumns = this.selectedColumns.reduce((arr: string[], it: string) => !it.startsWith('_') ? arr.concat(it) : arr, [])

    if (idToUpdate) {
      await fillgridService.updateView(this.template, idToUpdate, this.nameViewsToSave, metadataColumns, valuesColumns, this.predeterminedSelected)
        .then(() => {
          const viewItem = this.savedViewsList.find(it => it.id === idToUpdate)
          viewItem.name = this.nameViewsToSave
          viewItem.columns = this.selectedColumns
          this.nameViewsToSave = ''
          this.setViewUpdated(VIEW_STATE.SUCCESS)
        })
        .catch(() => {
          this.setViewUpdated(VIEW_STATE.FAILED)
        })
        .finally(() => {
          this.closeViewFeedback()
        })
    } else {
      await fillgridService.saveView(this.template, this.nameViewsToSave, metadataColumns, valuesColumns, this.predeterminedSelected)
        .then(datos => {
          this.savedViewsList.splice(0, 0, { id: datos.id, name: this.nameViewsToSave, columns: this.selectedColumns })
          this.savedViewSelected = datos.id
          this.nameViewsToSave = ''
          this.setViewUpdated(VIEW_STATE.SUCCESS)
        })
        .catch(() => {
          this.setViewUpdated(VIEW_STATE.FAILED)
        })
        .finally(() => {
          this.closeViewFeedback()
        })
    }
    this.updateBackupColumnsSelected([...this.selectedColumns])
    this.$bvModal.hide('save-views-modal')
  }

  saveViewsListInLocalStorage () {
    localStorage.setItem('bm-fillgrid-views-template-' + this.template, JSON.stringify(this.savedViewsList))
  }

  /**
   * Emits the event to update the list of columns selected
   * also the identifier of the view selected
   */
  apply () {
    this.$emit(
      'apply',
      {
        selection: this.columnNamesSelected(this.selectedColumns),
        viewId: this.savedViewSelected
      }
    )
  }

  /**
   * It returns the real name of the column to work them into the grid
   *
   * @param columns list of column names
   * @returns a list of columns names
   */
  columnNamesSelected (columns: any[]): any[] {
    return columns.map(col => FillgridColumn.getColumnName(col))
  }
}
